import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle2,
  Edit3,
  Globe2,
  History,
  Lock,
  Play,
  Plus,
} from "lucide-react";
import type { LifecycleRetreat } from "../../../../types/retreatLifecycle";
import {
  ChangedDataView,
  displayActor,
  eventLabel,
} from "../../../../utils/activityLogFormat";
import {
  SectionHeader,
  formatTentativeDates,
  statusChip,
  typeChip,
} from "./lifecycleUi";

type HistoryEntry = {
  id: number;
  eventType?: string;
  description?: string;
  byWhom?: string;
  actorName?: string;
  createdAt?: string;
  createdTime?: string;
  action?: string;
  changedDataParsed?: Record<string, any> | null;
  changedData?: string;
};

export function ProjectedRetreatsSection({
  rows,
  onProject,
  onEdit,
  onStartSetup,
  onOpenHistory,
  activeTypeFilter,
  onTypeFilterChange,
}: {
  rows: LifecycleRetreat[];
  onProject: () => void;
  onEdit: (r: LifecycleRetreat) => void;
  onStartSetup: (r: LifecycleRetreat) => void;
  onOpenHistory: (r: LifecycleRetreat) => void;
  activeTypeFilter?: string;
  onTypeFilterChange?: (type: string) => void;
}) {
  return (
    <Paper sx={{ mb: 4, overflow: "hidden" }}>
      <SectionHeader
        icon={<Globe2 size={22} color="#4c6782" />}
        title="Projected Retreats"
        subtitle="Executive Committee portfolio planning. These records are internal and not yet public."
        bgcolor="#eef4fa"
        action={
          <Button startIcon={<Plus size={16} />} variant="outlined" onClick={onProject}>
            Project Retreat
          </Button>
        }
      />

      <Box sx={{ p: 1.5, borderBottom: "1px solid #e5e7eb" }}>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
            Retreat type legend:
          </Typography>
          {(["Established Parish", "Outreach / New Parish", "Bishop-Sponsored"] as const).map((type) => (
            <React.Fragment key={type}>
              {typeChip(type, {
                onClick: onTypeFilterChange ? () => onTypeFilterChange(activeTypeFilter === type ? "" : type) : undefined,
                isSelected: activeTypeFilter === type,
                clickable: Boolean(onTypeFilterChange),
              })}
            </React.Fragment>
          ))}
          {activeTypeFilter && onTypeFilterChange && (
            <Button
              size="small"
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                py: 0,
                px: 1,
                ml: 1,
                color: "#6b7280",
                "&:hover": { color: "#111827" },
              }}
              onClick={() => onTypeFilterChange("")}
            >
              Clear filter
            </Button>
          )}
        </Stack>
      </Box>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Retreat</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Season</TableCell>
              <TableCell>Lead Couple</TableCell>
              <TableCell>Support</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No projected retreats yet. Click Project Retreat to begin planning.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((retreat) => (
              <TableRow key={retreat.id} hover sx={{ height: 48 }}>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit projected retreat">
                      <IconButton size="small" color="primary" onClick={() => onEdit(retreat)}>
                        <Edit3 size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        retreat.leadCoupleId
                          ? "Start Retreat Setup"
                          : "Assign a Lead Couple before starting setup"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="success"
                          disabled={!retreat.leadCoupleId}
                          onClick={() => onStartSetup(retreat)}
                        >
                          <Play size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Retreat history">
                      <IconButton size="small" onClick={() => onOpenHistory(retreat)}>
                        <History size={16} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: "#1265c4", fontWeight: 700 }}>
                    {retreat.parishName || retreat.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {retreat.parishLocation || retreat.location}
                  </Typography>
                </TableCell>
                <TableCell>{typeChip(retreat.retreatType)}</TableCell>
                <TableCell>{retreat.language}</TableCell>
                <TableCell>{retreat.seasonLabel}</TableCell>
                <TableCell>{retreat.leadCoupleName || "Not assigned"}</TableCell>
                <TableCell>{retreat.supportLabel || "—"}</TableCell>
                <TableCell>{statusChip(retreat.displayStatus || retreat.lifecycleStatus)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function SetupRetreatsSection({
  rows,
  onOpenSetup,
  onOpenHistory,
}: {
  rows: LifecycleRetreat[];
  onOpenSetup: (r: LifecycleRetreat) => void;
  onOpenHistory: (r: LifecycleRetreat) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <SectionHeader
        icon={<CheckCircle2 size={22} color="#2f7c3b" />}
        title="Retreats in Setup"
        subtitle="Lead Couples are completing the required publication checklist."
        bgcolor="#edf8ef"
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Retreat</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Lead Couple</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Publication Readiness</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No retreats in setup. Start setup from a Projected retreat after assigning a Lead Couple.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((retreat) => (
              <TableRow key={retreat.id} hover sx={{ height: 48 }}>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onOpenSetup(retreat)}
                      startIcon={<Edit3 size={14} />}
                    >
                      Open Setup
                    </Button>
                    <IconButton size="small" onClick={() => onOpenHistory(retreat)}>
                      <History size={16} />
                    </IconButton>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: "#1265c4", fontWeight: 700 }}>
                    {retreat.parishName || retreat.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {retreat.parishLocation || retreat.location}
                  </Typography>
                </TableCell>
                <TableCell>{retreat.language}</TableCell>
                <TableCell>
                  {formatTentativeDates(retreat.dateFrom, retreat.dateTo)}
                </TableCell>
                <TableCell>{retreat.leadCoupleName}</TableCell>
                <TableCell>
                  {statusChip(retreat.displayStatus || retreat.lifecycleStatus)}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <LinearProgress
                      variant="determinate"
                      value={retreat.readinessPercent ?? 0}
                      sx={{ width: 100, height: 8, borderRadius: 10 }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {retreat.readinessPercent ?? 0}%
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function RegistrationOpenSection({
  rows,
  search,
  onSearch,
  onOpenPublic,
  onEdit,
  onOpenHistory,
}: {
  rows: LifecycleRetreat[];
  search: string;
  onSearch: (v: string) => void;
  onOpenPublic: (r: LifecycleRetreat) => void;
  onEdit: (r: LifecycleRetreat) => void;
  onOpenHistory: (r: LifecycleRetreat) => void;
}) {
  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const hay = `${r.title} ${r.parishName} ${r.location} ${r.leadCoupleName}`.toLowerCase();
    return hay.includes(search.trim().toLowerCase());
  });

  return (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Registration Open</Typography>
          <Typography variant="body2" color="text.secondary">
            Public retreat pages currently accepting registrations.
          </Typography>
        </Box>
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search..."
          style={{
            minWidth: 230,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
          }}
        />
      </Box>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Retreat</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Lead Couple</TableCell>
              <TableCell>Support</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No registration-open retreats.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((retreat) => (
              <TableRow key={retreat.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" onClick={() => onEdit(retreat)}>
                      Manage
                    </Button>
                    <Button size="small" onClick={() => onOpenPublic(retreat)}>
                      Public Page
                    </Button>
                    <IconButton size="small" onClick={() => onOpenHistory(retreat)}>
                      <History size={16} />
                    </IconButton>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: "#1265c4", fontWeight: 700 }}>
                    {retreat.parishName || retreat.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {retreat.parishLocation || retreat.location}
                  </Typography>
                </TableCell>
                <TableCell>{retreat.language}</TableCell>
                <TableCell>
                  {formatTentativeDates(retreat.dateFrom, retreat.dateTo)}
                </TableCell>
                <TableCell>{retreat.leadCoupleName}</TableCell>
                <TableCell>{retreat.supportLabel || "—"}</TableCell>
                <TableCell>{statusChip(retreat.lifecycleStatus)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function ClosingSection({ rows }: { rows: LifecycleRetreat[] }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <SectionHeader
        icon={<Lock size={22} color="#70428d" />}
        title="Post-Retreat Closing"
        subtitle="Closure requirements, reminders, and temporary-access expiration."
        bgcolor="#f7f1fb"
      />
      <Box
        sx={{
          p: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No retreats currently in post-retreat closing.
          </Typography>
        )}
        {rows.map((retreat) => (
          <Paper key={retreat.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6">{retreat.parishName || retreat.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTentativeDates(retreat.dateFrom, retreat.dateTo)}
            </Typography>
            <Box sx={{ mt: 1 }}>{statusChip(retreat.lifecycleStatus)}</Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
}

export function StartSetupConfirmDialog({
  open,
  retreat,
  confirming,
  onClose,
  onConfirm,
}: {
  open: boolean;
  retreat: LifecycleRetreat | null;
  confirming?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Start Retreat Setup</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Confirming will move this retreat from Projected to Pre-Publication Setup,
          grant retreat-specific Lead Couple access, and email the Lead Couple a
          login link (no password).
        </Alert>
        <Stack spacing={1}>
          <Typography>
            <strong>Retreat:</strong> {retreat?.parishName || retreat?.title}
          </Typography>
          <Typography>
            <strong>Lead Couple:</strong> {retreat?.leadCoupleName}
          </Typography>
          <Typography>
            <strong>New status:</strong> Pre-Publication Setup
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={!!confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onConfirm}
          disabled={!!confirming || !retreat?.leadCoupleId}
        >
          {confirming ? "Starting…" : "Confirm & Start Setup"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RetreatHistoryDialog({
  open,
  retreat,
  entries,
  loading,
  onClose,
}: {
  open: boolean;
  retreat: LifecycleRetreat | null;
  entries: HistoryEntry[];
  loading?: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Retreat History — {retreat?.parishName || retreat?.title}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {!loading && entries.length === 0 && (
          <Typography color="text.secondary">No significant history yet.</Typography>
        )}
        <Stack spacing={0} sx={{ position: "relative", pl: 1 }}>
          {entries.map((e, idx) => {
            const when = e.createdTime || e.createdAt;
            const success = e.changedDataParsed?.success;
            return (
              <Box
                key={e.id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  pb: idx === entries.length - 1 ? 0 : 2,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor:
                      success === false
                        ? "#dc2626"
                        : success === true
                          ? "#16a34a"
                          : "#4c6782",
                    mt: 0.7,
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                />
                {idx < entries.length - 1 && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 5,
                      top: 16,
                      bottom: 0,
                      width: 2,
                      bgcolor: "#e5e7eb",
                    }}
                  />
                )}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    gap={0.5}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {eventLabel(e.eventType) || e.action || "Event"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {when ? new Date(when).toLocaleString() : ""}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    <ChangedDataView
                      raw={e.changedData}
                      parsed={e.changedDataParsed}
                    />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {displayActor(e)}
                    {e.action ? ` · ${e.action}` : ""}
                  </Typography>
                </Paper>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
